import React, {useEffect, useRef, useState} from "react";
import {observer} from 'mobx-react';
import GeneralDialog from "./GeneralDialog";
import {GenerateQRCode} from "../Shared/GenerateQRCode";
import FileSaver from 'file-saver';
import {useStores} from "../../Hooks";
import {Slider, Tooltip} from "@mui/material";
import ConditionalComponent from "../Shared/ConditionalComponent";
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import GlobalUtility from "../../Utilities/GlobalUtility";
import TruncatedWithTooltip from "../Shared/TruncatedWithTooltip"
import CircularProgress from '@mui/material/CircularProgress';
import Utility from "../../Utility";

export const GenerateQRModal = observer(() => {
    const {generalStore, modalStore, dataStore, projectStore, procedureStore} = useStores();

    const [imgURL, setImgURL] = useState(null);
    const [qrSize, setQrSize] = useState(145);
    const [qrSettings, setQrSettings] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [logoData, setLogoData] = useState(null);
    const lastGeneratedSignature = useRef("");
    const isQrPage = GlobalUtility.verifyLastPath('qr-code')

    const handleCopy = async () => {
        await navigator.clipboard.writeText(modalStore.modalData.shareLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };


    const maxScroll = 1024, minScroll = 145;
    const downloadQR = async () => {
        FileSaver.saveAs(imgURL, `QR_${modalStore.modalData.qrName}.jpg`);
    };

    const onEnter = () => {
        generalStore.getPlayerSettings().then(() => {
            setQrSettings({
                logo: dataStore.playerSettings.qrLogoImageAddress || null,
                topImage: dataStore.playerSettings.qrTopImageAddress || null,
                borderColor: dataStore.playerSettings.qrBorderColor || null,
            })
            setLoaded(true);
        });
    }

    const dialogStyle = {
        width: '400px',
        height: '762px',
        display: 'flex',
        flexDirection: 'column',
        padding: '20px',
        gap: '10px',
        margin: 'auto',
        alignItems: 'center',
    }

    const dialogFooter = (
        <div className="flex items-center justify-between w-full gap-5">
            <TruncatedWithTooltip text={modalStore.modalData.shareLink} className="flex-1 border-solid border-[1px] border-panels-normal text-sm text-main-normal rounded-input pt-1 pb-1 pr-3 pl-3" maxWidth="300px"/>
                <button
                    onClick={handleCopy}
                    className="flex items-center gap-2 text-sm text-main-normal hover:text-main-hover"
                >
                    {copied ? <CheckIcon className='text-link-normal' fontSize="small" /> : <ContentCopyIcon className='text-link-normal' fontSize="small" />}
                </button>
        </div>
    )
    useEffect(() => {
        setIsLoading(true)
    }, [modalStore.isGenerateQRModalOpen]);
    useEffect(() => {
        const message = generalStore.translate('generatingQr') + '...';
        if (isLoading && modalStore.isGenerateQRModalOpen) {
            modalStore.showInfoMessage(message);
        } else if (modalStore.alertMessage === message) {
            modalStore.alertMessage = '';
        }
    }, [isLoading, modalStore.isGenerateQRModalOpen]);
    useEffect(() => {
        if (!qrSettings?.logo) {
            return;
        }
        const loadLogo = async () => {
            let logo = qrSettings.logo;
            if (logo?.includes('https://')) {
                try {
                    const base64 = await Utility.fetchFileAsBase64(logo);
                    setLogoData(base64);
                } catch (error) {
                    console.error('Failed to fetch logo:', error);
                }
            } else {
                setLogoData(logo);
            }
        };
        loadLogo();
    }, [qrSettings?.logo]);
    return (
        <GeneralDialog
            openControl='isGenerateQRModalOpen'
            dialogStyle={dialogStyle}
            onEnter={onEnter}
            title='QR CODE'
            extraClose={true}
            noCancel={true}
            headerStyle={{
                color: 'var(--main-normal-color) !important',
                fontWeight:'bold',
                fontSize: '24px',
            }}
            bodyClassName='w-full !pt-0'
            iconConfirmation={<DownloadIcon className='w-6 pr-1'/>}
            body={
                <ConditionalComponent if={loaded}>
                    <div className='test'>
                        <p className='text-main-normal text-sm text-center'>
                            Use the slider to change the size of the QR <br/>
                            Download to print or share with team members
                        </p>
                        <Slider
                            value={qrSize}
                            onChange={(e, newSize) => setQrSize(newSize)}
                            valueLabelDisplay="auto"
                            min={minScroll}
                            max={maxScroll}
                        />
                    </div>
                    <div className='generate-qr-container'>
                        <GenerateQRCode
                            link={modalStore.modalData.shareLink || ""}
                            projectName={projectStore.currentProject?.ProjectName}
                            itemName={isQrPage ? modalStore.modalData.qrName : procedureStore.currentProcedure?.ProcedureName}
                            attachedItemName={modalStore.modalData.attachedItemName}
                            logo={qrSettings?.logo || null}
                            topImage={qrSettings?.topImage || null}
                            borderColor={qrSettings?.borderColor || null}
                            setImgURL={setImgURL}
                            size={qrSize}
                            maxScroll={maxScroll}
                            minScroll={minScroll}
                            setIsLoading={setIsLoading}
                            logoData={logoData}
                            lastGeneratedSignature={lastGeneratedSignature}
                        />
                    </div>
                    <ConditionalComponent if={!!imgURL || isLoading}>
                        <div className='qr-jpeg-container'>
                            {isLoading ? ( <div style={{ margin: 20 }}> <CircularProgress size={32} /> </div> ): (
                            <img src={imgURL} alt='qr-code' className="qr-jpeg" crossOrigin="anonymous"/>)}
                        </div>
                    </ConditionalComponent>
                    <ConditionalComponent if={!generalStore.isShowPinCode}>
                        <div className='border-solid border-[1px] border-panels-normal text-sm text-main-normal rounded-input p-[10px]'>{modalStore.modalData.qrName}</div>
                    </ConditionalComponent>
                    <ConditionalComponent if={generalStore.isShowPinCode}>
                    <div className='flex flex-col items-center justify-center gap-2'>
                        <div className='text-xl'>
                            {generalStore.translate('personal') + ' PIN  ' + generalStore.translate('code', true)}
                        </div>
                        <div className='font-bold text-4xl'>
                            {generalStore.userDetails.QRCode}
                        </div>
                    </div>
                </ConditionalComponent>
                </ConditionalComponent>
            }
            dialogFooter={generalStore.isShowPinCode ? '' : dialogFooter}
            isConfirmDisabled={!modalStore.modalData.shareLink}
            onConfirm={downloadQR}
            confirmText={generalStore.translate('download')}
        />
    )
});
